import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteConfig, stats, testimonials, faqs } from '@/mocks/welfare';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qmotue3oq4jqumtn50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试。');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        email: siteConfig.email,
        telephone: siteConfig.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteConfig.address,
          addressLocality: '北京',
          addressRegion: '海淀区',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'WebPage',
        name: `关于我们 - ${siteConfig.name}`,
        description: `${siteConfig.name}品牌介绍、用户口碑及联系方式。${siteConfig.description}`,
        url: `${siteConfig.url}/about`,
        isPartOf: {
          '@type': 'WebSite',
          name: siteConfig.name,
          url: siteConfig.url,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.name}`}
        description={`${siteConfig.name}品牌介绍、用户口碑及联系方式。${siteConfig.description}`}
        keywords={`${siteConfig.keywords},关于我们,品牌介绍,联系方式`}
        canonicalUrl={`${siteConfig.url}/about`}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">关于福利导航网</h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                福利导航网成立于2024年，是国内领先的一站式福利资源聚合平台。我们致力于为用户汇集全网最优质的优惠券、折扣活动、会员福利、返利信息及限时特惠，让每一位用户都能轻松省钱，享受品质生活。我们的团队每天实时监测各大平台的福利信息，确保第一时间更新，帮助用户不错过任何一个省钱机会。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '真实可靠',
                  desc: '所有福利信息均经过人工核实，确保每条优惠真实有效，用户可放心领取使用。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '实时更新',
                  desc: '团队全天候监测各大平台，福利信息第一时间同步更新，绝不让您错过任何优惠。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '用户至上',
                  desc: '以用户需求为核心，不断优化平台体验，提供最便捷的福利查找和使用体验。',
                },
              ].map((feat) => (
                <div key={feat.title} className="text-center p-8 rounded-xl bg-background-50 border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${feat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonials" className="py-10 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">用户口碑</h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">来自真实用户的使用评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl border border-background-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.date}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-secondary-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-8">
                  如有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们将在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">电子邮箱</div>
                      <a href={`mailto:${siteConfig.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">{siteConfig.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">客服电话</div>
                      <a href={`tel:${siteConfig.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">{siteConfig.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">公司地址</div>
                      <span className="text-sm text-foreground-500">{siteConfig.address}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} data-readdy-form className="bg-background-50 border border-background-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-foreground-900 mb-4">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg text-sm text-accent-700">
                      <i className="ri-checkbox-circle-line mr-1"></i> 消息发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                      <i className="ri-error-warning-line mr-1"></i> {formError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">消息内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您想说的内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">常见问题</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 border border-background-200 rounded-xl p-5">
                  <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                    <a id={`faq-${index}`} className="no-underline">{faq.question}</a>
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 text-center">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">准备好开始省钱了吗？</h2>
            <p className="text-sm text-foreground-500 mb-6">立即浏览福利导航网，发现海量优惠资源</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-lg text-base font-medium hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap"
            >
              开始探索 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}